import { Shape } from "@kosmod/pancake";
import { TheatreState } from "./useTheatreState";

import { ToolID } from "../defs/tools/define";
import { tools, ToolState } from "../defs/tools";

import { Ref, useEffect, useMemo, useState } from "@lukekaalim/act";
import { assetsURLs } from "../assets";
import { ID, useJeffect, useLogger, Vector } from "@kosmod/kit";
import { renderBeat } from "@kosmod/graphics-3d";
import { TheatreController, TheatreData } from "./useTheatreController";
import { TheatreSelection, TheatreSelectionController } from "./useTheatreSelection";
import { Mini, Surface } from "../defs";
import { getHandlerForTool, ToolHandler } from "../components";
import { Color } from "three";
import { TBNViewMatrix } from "three/src/nodes/TSL.js";

export type ToolController = {
  toolbelts: ToolBelt[],
  activeToolBelt: null | ToolBeltID,
  temporaryTool: null | ToolState,

  primaryTools: ToolState[],
  hiddenTools: null | ToolState[],

  allTools: ToolState[],
  activeTool: ToolID | null,

  setActiveToolBelt(toolBeltId: ToolBeltID | null): void,
  
  setActiveTool(id: ToolID | null): void,
  setToolState(id: ToolID, state: ToolState): void,

  getActiveTool(): null | { state: ToolState, handler: ToolHandler },

  setToolbelt(belt: ToolBeltID, values: Partial<Omit<ToolBelt, 'id'>>): void,
  addToolbelt(): void,
  removeToolbelt(belt: ToolBeltID): void,
};

export const createToolsForSelectedMini = (mini: Mini): [ToolID, ToolState[]] => {
  const defaultToolId = ID('theatre:tool');

  return [defaultToolId, [
    { id: ID('theatre:tool'), type: 'selection:deselect' },
    { id: defaultToolId, type: 'minis:move', miniId: mini.id },
    tools.create('minis:delete', { miniId: mini.id }),

    ...mini.logic.flatMap((logic, i): ToolState[] => {

      switch (logic.key) {
        case 'door':
          // return [{ type: 'minis:door:toggle', logicIndex: i, miniId: mini.id, id: ID('theatre:tool') }]
        case 'hitpoints':
          // return [{ type: 'minis:hitpoints:damage', id: ID('theatre:tool') }]
      }

      return [];
    })
  ]]
}

export const createToolsForSelectedTile = (tiles: Vector<2>[], gridId: Surface["id"]) => {
  return [

  ]
}

export type ToolBeltID = ID<'toolbelt'>;
export type ToolBelt = {
  name: string,
  color: Color,
  id: ToolBeltID,
  tools: ToolState[],
}

const buildInitialToolBelts = (data: TheatreData): ToolBelt[] => {
  return [
    { name: 'Dice', id: ID('toolbelt'), color: new Color('#b40e0e'), tools: [
      tools.create('selection:select', { filter: 'die', operation: 'replace' }),
      tools.create('dice:cleanup', { radius: 1 }),
      ...[...data.diceTemplates.values()].map(template => 
        tools.create('dice:roll', { diceTemplateId: template.id }),
      ),
    ] },

    { name: 'Minis', id: ID('toolbelt'), color: new Color('#4650d8'), tools: [
      tools.create('selection:select', { filter: 'mini', operation: 'replace' }),
      ...[...data.miniTemplates.values()].map(template => 
        tools.create('minis:place', {
          source: { type: 'template', miniTemplateId: template.id },
          placementCounter: 0
        }),
      ),
    ] },

    { name: 'Terrain', id: ID('toolbelt'), color: new Color('#da4690'), tools: [
      tools.create('selection:select', { filter: 'terrain', operation: 'replace' }),
      tools.create('terrain:paint', { brush: null, start: null, part: 'floor' }),
      ...[...data.brushes.values()].flatMap((brush): ToolState[] => [
        tools.create('terrain:paint', { brush: brush.id, start: null, part: 'floor' }),
      ]),
    ] },

    { name: 'Debug', id: ID('toolbelt'), color: new Color('#55a708'), tools: [
      tools.create('dev:vis', { canvas: 'interior' }),
    ] },
  ]
}

export const useToolController = (
  selection: TheatreSelectionController,
  theatreController: TheatreController,
  data: TheatreData,
) => {
  const logger = useLogger('ToolController');
  
  const [toolbelts, setToolBelts] = useState<ToolBelt[]>(() => buildInitialToolBelts(data))

  const [activeToolBeltId, setActiveToolBelt] = useState<ToolBeltID | null>(() => toolbelts[0].id)

  const activeToolBelt = toolbelts.find(t => activeToolBeltId === t.id);


  const [selectionToolbelt, setSelectionToolbelt] = useState<ToolBelt | null>(null)

  const primaryTools = selectionToolbelt ? selectionToolbelt.tools
    : activeToolBelt?.tools
    || []

  const [activeTool, setActiveTool] = useState<null | ToolID>(() => primaryTools[0].id)


  const allTools = [...toolbelts, selectionToolbelt].filter(x => !!x).flatMap(belt => belt.tools)

  useEffect(() => {
    const sub = selection.onSelectionChange.subscribe(nextSelection => {
      switch (nextSelection.type) {
        case 'none':
          if (activeToolBelt) {
            // set the tool back to the default
            setActiveTool(activeToolBelt.tools[0]?.id);
          }
          return setSelectionToolbelt(null);
        case 'mini':
          const mini = data.minis.find(m => nextSelection.miniId === m.current.id);
          if (!mini)
            throw new Error();

          const [nextActiveTool, nextSelectionTools] = createToolsForSelectedMini(mini.current)
          setSelectionToolbelt({
            name: 'Selection',
            id: ID('toolbelt'),
            tools: nextSelectionTools,
            color: new Color('white'),
          })
          setActiveTool(nextActiveTool);
          return;
      }
    });

    return () => sub.unsubscribe();
  }, [selection.onSelectionChange, data.minis]);

  useEffect(() => {
    const state = getToolState(activeTool);
    theatreController.setTool(state)
  }, [])

  const getToolState = (toolId: ToolID | null) => {
    return allTools
      .find(tool => tool.id === toolId) || null;
  }
  const getToolHandler = (state: ToolState) => {
    return getHandlerForTool(state.type)
  }

  const controller = useMemo((): ToolController => {
    return {
      allTools,
      activeToolBelt: selectionToolbelt ? null : activeToolBeltId,
      setActiveToolBelt(nextToolbeltId) {

        if (nextToolbeltId) {
          const nextToolbelt = toolbelts.find(t => t.id === nextToolbeltId);
          if (nextToolbelt) {
            setActiveTool(nextToolbelt.tools[0]?.id);
          }
        } else {
          setActiveTool(null)
        }

        setActiveToolBelt(nextToolbeltId)
      },
      toolbelts,

      primaryTools,
      temporaryTool: null,
      hiddenTools: [],

      activeTool,
      
      setActiveTool(toolId: ToolID) {
        const state = getToolState(toolId);
        const handler = state && getToolHandler(state);

        if (!state || !handler)
          return null;
        
        if (handler.onSelect) {
          const isSelected = handler.onSelect(
            state, controller, selection, logger.createSubsystem(state.type),
            theatreController,
            data,
          );
          if (isSelected === false)
            return;
        }
        setActiveTool(toolId);
        theatreController.setTool(state)
      },

      setToolState(id, state) {
        setToolBelts(toolBelts => {
          return toolBelts.map(toolbelt => {
            return {
              ...toolbelt,
              tools: toolbelt.tools.map(tool => tool.id === id ? { ...tool, ...state } : tool)
            }
          })
        })

        // setGlobalTools(toolStates => toolStates.map(t => t.id === id ? state : t))
        
        // update the server if if this our active tool
        if (id === activeTool) {
          theatreController.setTool(state)
        }
      },

      getActiveTool() {
        const state = getToolState(activeTool);
        const handler = state && getToolHandler(state);
        if (!state || !handler)
          return null;
        
        return { state, handler };
      },

      setToolbelt(beltId, { name = null, color = null, tools = null }) {
        setToolBelts(belts => {
          return belts.map((tb): ToolBelt => {
            if (tb.id === beltId) {
              return {
                id: tb.id,
                name: name === null ? tb.name : name,
                color: color || tb.color,
                tools: tools || tb.tools
              }
            }
            return tb;
          })
        })
      },
      addToolbelt() {
        setToolBelts(belts => [...belts, {
          id: ID('toolbelt'),
          name: 'New Toolbelt',
          color: new Color(Math.random(), Math.random(), Math.random()),
          tools: [],
        }])
      },
      removeToolbelt(beltId) {
        setToolBelts(belts => belts.filter(tb => tb.id !== beltId));
      },
    }
  }, [activeTool, selection, activeToolBeltId, toolbelts, allTools]);

  return controller;
};

export const useActiveToolHandlers = (theatre: TheatreState) => {
  const logger = useLogger('ToolHandler');

  const onRender = renderBeat.useBeat();

  useJeffect((janitor) => {
    const tool = theatre.tools.getActiveTool();
    if (!tool)
      return;
    const { onInput, onUpdate } = tool.handler;

    if (onInput) {
      janitor.add(theatre.events.pointerClick.subscribe((event) => {
        if (event.defaultPrevented)
          return;

        onInput({ type: 'mouse', button: 'primary', event }, tool.state, theatre, logger.createSubsystem(tool.state.type), event);
      }))
      janitor.add(theatre.events.contextClick.subscribe((event) => {
        if (event.defaultPrevented)
          return;
        
        onInput({ type: 'mouse', button: 'secondary', event }, tool.state, theatre, logger.createSubsystem(tool.state.type), event);
      }))
    }

    if (onUpdate) {
      janitor.add(onRender(() => onUpdate(tool.state, theatre)))
    }

  }, [theatre])

  useJeffect(j => {
    const tool = theatre.tools.getActiveTool();

    const onDigitHotkeyPress = (digit: number, event: KeyboardEvent) => {
      if (event.ctrlKey) {
        const toolbelt = theatre.tools.toolbelts[digit - 1];
        theatre.tools.setActiveToolBelt(toolbelt.id);
        return;
      }
      const tool = theatre.tools.primaryTools[digit - 1];
      if (tool)
        theatre.tools.setActiveTool(tool.id);
    }

    j.add(theatre.keyboard.onKeyPress.subscribe((key) => {
      switch (key.code) {
        case 'Digit1':
          return onDigitHotkeyPress(1, key);
        case 'Digit2':
          return onDigitHotkeyPress(2, key);
        case 'Digit3':
          return onDigitHotkeyPress(3, key);
        case 'Digit4':
          return onDigitHotkeyPress(4, key);
        case 'Digit5':
          return onDigitHotkeyPress(5, key);
        case 'Digit6':
          return onDigitHotkeyPress(6, key)
        case 'Digit7':
          return onDigitHotkeyPress(7, key);
        case 'Digit8':
          return onDigitHotkeyPress(8, key);
        case 'Digit9':
          return onDigitHotkeyPress(9, key);
        case 'Digit0':
          return onDigitHotkeyPress(10, key);
        default: {
          if (tool && tool.handler.onInput) {
            tool.handler.onInput({ type: 'keyboard', code: key.code }, tool.state, theatre, logger.createSubsystem(tool.state.type), key)
          }
        }
      }
    }))
  }, [theatre.tools])
}